import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { ActivityGroupStatus } from '../../interfaces/ActivityGroupStatus.enum';
import { requiredStringSchema, calendarDateField, tzHeadersSchema, sseQuerySchema } from '../schemas';

// ============================================================
// Reusable Schemas (mirroring activitiesGroup.schemas.ts)
// ============================================================

const projectParamsSchema = z.object({
	projectId: requiredStringSchema.describe('Project identifier'),
});

const groupIdParamsSchema = projectParamsSchema.extend({
	groupId: requiredStringSchema.describe('Activity group ID'),
});

const endDateQuerySchema = z.object({
	endDate: calendarDateField.describe('End date'),
});

const startDateQuerySchema = z.object({
	startDate: calendarDateField.describe('Start date'),
});

const putGroupActivitiesBodySchema = z.object({
	areas: z.array(z.any()).optional().describe('Areas'),
	description: z.string().optional().describe('Description'),
	endDate: calendarDateField.optional().describe('End date'),
	floor: z.object({}).passthrough().optional().describe('Floor object'),
	floors: z.array(z.any()).optional().describe('Floors array'),
	groupId: z.string().optional().describe('Group ID'),
	label: z.any().optional().describe('Label'),
	assignee: z.string().nullable().optional().describe('Assignee'),
	profession: z.object({}).passthrough().optional().describe('Profession object'),
	startDate: calendarDateField.optional().describe('Start date'),
	dangerousActivity: z.boolean().optional().describe('Is dangerous activity'),
	status: z.enum(ActivityGroupStatus).optional().describe('Status'),
	linkedSequenceType: z.string().nullable().optional().describe('Linked sequence type'),
});

const getCurrentGroupActivitiesStaticDataQuerySchema = z.object({
	professionId: z.string().optional().describe('Optional profession filter'),
});

const getMainPageWidgetActivitiesQuerySchema = z.object({
	professionIds: z.array(z.string()).optional().describe('Optional profession IDs filter'),
	areaId: z.string().optional().describe('Optional area filter'),
	floorId: z.string().optional().describe('Optional floor filter'),
});

/**
 * `groupStatus` is a COMMA-SEPARATED list, not one value: the handler does
 * `String(query.groupStatus).split(',')` and validates each part against the
 * enum. Typed as a bare `z.enum` it rejected every multi-status call with a 400
 * before the handler ran — which is what mobile sends, and nothing noticed
 * because mobile built the URL as a string. Validated here as a CSV so the
 * contract and the handler agree, and the handler keeps receiving a string.
 */
const groupStatusCsvSchema = z
	.string()
	.refine(
		(value) =>
			value
				.split(',')
				.every((status) => Object.values(ActivityGroupStatus).includes(status as ActivityGroupStatus)),
		{ message: 'groupStatus must be a comma-separated list of ActivityGroupStatus values' }
	)
	.describe('Optional comma-separated group status filter');

const getCurrentGroupActivitiesRealtimeDataByFloorsQuerySchema = z.object({
	groupStatus: groupStatusCsvSchema.optional(),
	professionId: z.string().optional().describe('Optional profession filter'),
});

const getWidgetActivitiesLocationCountQuerySchema = z.object({
	floorIds: z.array(z.string()).optional().describe('Optional floor IDs filter'),
	professionIds: z.array(z.string()).optional().describe('Optional profession IDs filter'),
});

const getMainPageWidgetActivitiesStreamQuerySchema = sseQuerySchema.extend({
	projectId: requiredStringSchema.describe('Project ID'),
});

// ============================================================
// SSE Route (cannot be in defineContract - exported separately)
// ============================================================

/**
 * GET /mainPageWidgetActivities/stream - SSE stream for main page widget activities
 */
export const activitiesGroupStreamRoute = route({
	method: 'GET',
	path: '/groups/mainPageWidgetActivities/stream',
	query: getMainPageWidgetActivitiesStreamQuerySchema,
	response: z.any().describe('SSE stream of main page widget activities'),
	operationId: 'getMainPageWidgetActivitiesStream',
	tags: ['Activities Groups'],
	summary: 'Stream main page widget activities (SSE)',
	description: 'Server-Sent Events stream for real-time main page widget activity updates',
});

// ============================================================
// Non-SSE Routes Contract
// Route definition order must match registration order:
// specific paths BEFORE /:groupId
// ============================================================

/**
 * `{ root: true }` so the project can precede the collection segment. The SSE
 * route above therefore spells `/groups` itself — without that, making this
 * contract root would silently move a URL the frontend builds as a string.
 */
export const activitiesGroupContract = defineContract(
	'groups',
	{
		getOngoingAndCompleteTodayActivitiesInfo: route({
			method: 'GET',
			path: '/projects/:projectId/groups/todayRealtimeInfo',
			params: projectParamsSchema,
			headers: tzHeadersSchema,
			query: getCurrentGroupActivitiesStaticDataQuerySchema,
			response: z.any().describe('Array of IActivityRealtimeInfo'),
			operationId: 'getOngoingAndCompleteTodayActivitiesInfo',
			tags: ['Activities Groups'],
			summary: 'Get current group activities static data',
			description: 'Retrieves static data for current group activities',
		}),
		getMainPageWidgetActivities: route({
			method: 'GET',
			path: '/projects/:projectId/groups/mainPageWidget',
			params: projectParamsSchema,
			headers: tzHeadersSchema,
			query: getMainPageWidgetActivitiesQuerySchema,
			response: z.any().describe('Array of IMainPageWidgetActivityData'),
			operationId: 'getMainPageWidgetActivities',
			tags: ['Activities Groups'],
			summary: 'Get main page widget activities',
			description: 'Retrieves activities for the main page widget with optional filters',
		}),
		getWidgetActivitiesCountByLocation: route({
			method: 'GET',
			path: '/projects/:projectId/groups/widgetActivities/byLocation/count',
			params: projectParamsSchema,
			headers: tzHeadersSchema,
			query: getWidgetActivitiesLocationCountQuerySchema,
			response: z.any().describe('IResourceCountByLocation - Activity counts by location'),
			operationId: 'getWidgetActivitiesCountByLocation',
			tags: ['Activities Groups'],
			summary: 'Get widget activities location count',
			description: 'Retrieves activity location counts for widget display',
		}),
		getMainPageWidgetActivitiesOfGroup: route({
			method: 'GET',
			path: '/projects/:projectId/groups/mainPageWidget/:groupId',
			params: groupIdParamsSchema,
			response: z.any().describe('IMainPageWidgetActivityData'),
			operationId: 'getMainPageWidgetActivitiesOfGroup',
			tags: ['Activities Groups'],
			summary: 'Get main page widget activities of group',
			description: 'Retrieves main page widget activities for a specific group',
		}),
		getOngoingAndCompleteTodayActivitiesInfoByFloors: route({
			method: 'GET',
			path: '/projects/:projectId/groups/todayRealtimeInfoByFloors',
			params: projectParamsSchema,
			headers: tzHeadersSchema,
			query: getCurrentGroupActivitiesRealtimeDataByFloorsQuerySchema,
			response: z.any().describe('IActivityRealtimeInfoByFloors - Realtime activity info organized by floor IDs'),
			operationId: 'getOngoingAndCompleteTodayActivitiesInfoByFloors',
			tags: ['Activities Groups'],
			summary: 'Get current group activities realtime data by floors',
			description: 'Retrieves realtime data for current group activities organized by floors',
		}),
		getActivityGroupIssues: route({
			method: 'GET',
			path: '/projects/:projectId/groups/:groupId/issues',
			params: groupIdParamsSchema,
			response: z.any().describe('Array of issues (IIssue[])'),
			operationId: 'getActivityGroupIssues',
			tags: ['Activities Groups'],
			summary: 'Get group issues',
			description: 'Retrieves issues associated with an activity group',
		}),
		completeActivityGroup: route({
			method: 'PUT',
			path: '/projects/:projectId/groups/:groupId/complete',
			params: groupIdParamsSchema,
			headers: tzHeadersSchema,
			response: z
				.any()
				.describe('IActivitiesGroup - Activity group with status, dates, profession, and optional label'),
			operationId: 'completeActivityGroup',
			tags: ['Activities Groups'],
			summary: 'Complete activity group',
			description: 'Marks an activity group as complete',
		}),
		startActivityGroup: route({
			method: 'PUT',
			path: '/projects/:projectId/groups/:groupId/start',
			params: groupIdParamsSchema,
			headers: tzHeadersSchema,
			response: z
				.any()
				.describe('IActivitiesGroup - Activity group with status, dates, profession, and optional label'),
			operationId: 'startActivityGroup',
			tags: ['Activities Groups'],
			summary: 'Start activity group',
			description: 'Starts an activity group',
		}),
		extendActivityGroup: route({
			method: 'PUT',
			path: '/projects/:projectId/groups/:groupId/extend',
			params: groupIdParamsSchema,
			headers: tzHeadersSchema,
			query: endDateQuerySchema,
			response: z
				.any()
				.describe('IActivitiesGroup - Activity group with status, dates, profession, and optional label'),
			operationId: 'extendActivityGroup',
			tags: ['Activities Groups'],
			summary: 'Extend activity group',
			description: 'Extends the end date of an activity group',
		}),
		editActivityGroupDate: route({
			method: 'PUT',
			path: '/projects/:projectId/groups/:groupId/editDate',
			params: groupIdParamsSchema,
			headers: tzHeadersSchema,
			query: endDateQuerySchema,
			response: z
				.any()
				.describe('IActivitiesGroup - Activity group with status, dates, profession, and optional label'),
			operationId: 'editActivityGroupDate',
			tags: ['Activities Groups'],
			summary: 'Edit activity group date',
			description: 'Edits the end date of an activity group',
		}),
		pushActivityGroup: route({
			method: 'PUT',
			path: '/projects/:projectId/groups/:groupId/push',
			params: groupIdParamsSchema,
			headers: tzHeadersSchema,
			query: startDateQuerySchema,
			response: z
				.any()
				.describe('IActivitiesGroup - Activity group with status, dates, profession, and optional label'),
			operationId: 'pushActivityGroup',
			tags: ['Activities Groups'],
			summary: 'Push activity group',
			description: 'Pushes the start date of an activity group',
		}),
		updateActivityGroup: route({
			method: 'PUT',
			path: '/projects/:projectId/groups/:groupId',
			params: groupIdParamsSchema,
			headers: tzHeadersSchema,
			body: putGroupActivitiesBodySchema,
			response: z.any().describe('Array of IWorkplanActivity'),
			operationId: 'updateActivityGroup',
			tags: ['Activities Groups'],
			summary: 'Update activity group',
			description: 'Updates an existing activity group with the provided data',
		}),
		deleteActivityGroup: route({
			method: 'DELETE',
			path: '/projects/:projectId/groups/:groupId',
			params: groupIdParamsSchema,
			headers: tzHeadersSchema,
			response: z.string().describe('Deletion confirmation message'),
			operationId: 'deleteActivityGroup',
			tags: ['Activities Groups'],
			summary: 'Delete activity group',
			description: 'Deletes an activity group by its ID',
		}),
		getActivityGroupById: route({
			method: 'GET',
			path: '/projects/:projectId/groups/:groupId',
			params: groupIdParamsSchema,
			response: z.any().describe('IActivityGroup - Activity group with floors and activities'),
			operationId: 'getActivityGroupById',
			tags: ['Activities Groups'],
			summary: 'Get activity group by ID',
			description: 'Retrieves a specific activity group by its ID',
		}),
	},
	{ root: true }
);
